<script setup lang="ts">
import { toRef } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import {
  activeTab,
  isAnalysisTab,
  setActiveTab,
  type ObservationsDrawerTab,
} from "@/stores/ObservationsPanelState";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { useGetFieldObservations } from "@/stores/queries/field-observations.query";
import { trackTab } from "@/analytics/drawing-analytics";

/**
 * Onglets du volet Observations, fixes sous son titre : IA (groupes détectés
 * par l'analyse, violet), Observations (celles réellement créées, bleu) et
 * Parties (leur classement par partie d'ouvrage). Le volet décide lesquels
 * sont proposés (`availableDrawerTabs`) et n'affiche pas la barre s'il n'en
 * reste qu'un.
 */

const props = defineProps<{
  /** Onglets proposés, dans l'ordre d'affichage. */
  tabs: ObservationsDrawerTab[];
  /** Ouvrage lié au plan (0 = aucun) — pour le compteur d'observations. */
  diagnosticId: number;
}>();

const { t } = useTranslation();
const analysis = usePlanAnalysisState();
const { data: observations } = useGetFieldObservations(
  toRef(props, "diagnosticId"),
);

/**
 * Entrer dans l'onglet IA vaut une ouverture de la famille « analyse » de la
 * page Usage — l'équivalent du clic sur l'ancien onglet Analyse.
 */
const selectTab = (tab: ObservationsDrawerTab): void => {
  if (isAnalysisTab(tab) && !isAnalysisTab(activeTab.value)) {
    trackTab("analysis", "opened");
  }
  setActiveTab(tab);
};
</script>

<template>
  <div class="tabs-group d-flex">
    <button
      v-if="tabs.includes('ai')"
      type="button"
      class="tab-btn tab-ai d-inline-flex align-items-center justify-content-center gap-1"
      :class="{ active: activeTab === 'ai' }"
      @click="selectTab('ai')"
    >
      <PhosphorIcon name="sparkle" :size="13" weight="fill" />
      {{ t("canvas:observations.tabs.ai") }}
      <span class="tab-count">{{ analysis.groups.length }}</span>
    </button>
    <button
      type="button"
      class="tab-btn d-inline-flex align-items-center justify-content-center gap-1"
      :class="{ active: activeTab === 'observations' }"
      @click="selectTab('observations')"
    >
      {{ t("canvas:observations.tabs.observations") }}
      <span v-if="observations" class="tab-count">
        {{ observations.length }}
      </span>
    </button>
    <button
      v-if="tabs.includes('parts')"
      type="button"
      class="tab-btn"
      :class="{ active: activeTab === 'parts' }"
      @click="selectTab('parts')"
    >
      {{ t("canvas:observations.tabs.parts") }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
.tabs-group {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.tab-btn {
  flex: 1;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  opacity: 0.75;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 5px 8px;
  cursor: pointer;
  white-space: nowrap;

  &.active {
    background-color: var(--bs-primary);
    color: white;
    opacity: 1;
  }

  // L'onglet IA garde l'accent violet de tout ce qui vient de l'analyse.
  &.tab-ai.active {
    background-color: var(--dr-report);
  }
}

.tab-count {
  font-weight: 500;
  opacity: 0.8;
}
</style>
